import {TPMEventBO} from "./TPMEventBO";
import {ASync} from "../PA.Core/Basic/ASync";
import {TPMEventSpend} from "./TPMEventBO.ITPMEventSpend";
import {IPolicy} from "../PA.Core/PA.Core.IF.Policy";
import {BehaviorSubject} from "rxjs";
import {WB_EventTypeRow} from "../PA.Core/PAdbSchemaRows";
import {UserSecurityService} from "../PA.Security.Services/UserSecurityService";
import * as _ from 'lodash'
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {IUsrContext} from "../PA.Core/PA.Core.IF.User";
import {Role_TPM_Event_Approver, Role_TPM_Event_Editor} from "../PA.Core/Security/Roles";
import {ILock} from "../PA.Concurrency.Core/PA.Concurrency.IF.Lock";
import {Subscription} from "rxjs/Subscription";

export class TPMEventBOStandardPolicy
    implements IPolicy {
    _tpmEvent: TPMEventBO
    _evtType: WB_EventTypeRow
    _PolicyChanged: BehaviorSubject<IPolicy>
    _onLockPropertyChanged: Subscription
    _isApprover: boolean
    _isEditor: boolean
    _isClosedOrCancelled: boolean
    _isPostApproval: boolean
    _isPreApproval: boolean
    _isLoaded: boolean
    _Lock: ILock

    constructor(tpmEvent: TPMEventBO, aLock?: ILock) {
        this._tpmEvent = tpmEvent
        this._evtType = tpmEvent.WB_TPMEvent.WB_EventTypeRow
        this._PolicyChanged = new BehaviorSubject<IPolicy>(this)
        this._isApprover = false
        this._isEditor = false
        this._isClosedOrCancelled = false
        this._isPostApproval = false
        this._isPreApproval = false
        this._isLoaded = false

        this._Lock = aLock

        if (!_.isEmpty(aLock)) {
            this._onLockPropertyChanged = aLock.OnPropertyChanged.subscribe(x => this._PolicyChanged.next(this))
        }
    }

    get OnLoadComplete(): Promise<void> {
        const self = this

        if (self._isLoaded) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {
            self._tpmEvent.PrimaryWorkflowProcessor.OnLoadComplete
                .then((ignore) => {
                    let currentState = self._tpmEvent.PrimaryWorkflowProcessor.CurrentState
                    self._isClosedOrCancelled = currentState.StatusTypeClosed || currentState.StatusTypeCancelled
                    self._isPostApproval = currentState.StatusTypeIsPostApproval
                    self._isPreApproval = currentState.StatusTypeIsPreApproval
                    return UserSecurityService.getUserGroupPlanList()
                })
                .then((arrWB_PlanIDs) => {
                    let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
                    if (_.includes(arrWB_PlanIDs, self._tpmEvent.WB_TPMEvent.cif.WB_PLAN_ID) && usrContext) {
                        self._isEditor = usrContext.hasRoleId(Role_TPM_Event_Editor, self._tpmEvent.WB_TPMEvent.cif.WB_CUST_ID_PLANNING)
                        self._isApprover = usrContext.hasRoleId(Role_TPM_Event_Approver, self._tpmEvent.WB_TPMEvent.cif.WB_CUST_ID_PLANNING)
                    } else {
                        self._isApprover = false
                        self._isEditor = false
                    }
                    self._isLoaded = true
                    self._PolicyChanged.next(self)
                    resolve()
                })
                .catch((err) => {
                    self._isApprover = false
                    self._isEditor = false
                    self._PolicyChanged.next(self)
                    error(err)
                })
            //

        })
    }

    get bIsApprover(): boolean {
        return this._isApprover
    }

    get bIsEditor(): boolean {
        return this._isEditor
    }

    get bIsClosedOrCancelled(): boolean {
        return this._isClosedOrCancelled
    }

    get bIsPreApproval(): boolean {
        return this._isPreApproval
    }

    get bIsPostApproval(): boolean {
        return this._isPostApproval
    }

    // Add Editbility/ Visibility Properties for all the Elements of an Event
    get bNameEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bPlanningCustomerVisible(): boolean {
        return this._evtType.bPlanningCustomerShowInd
    }


    get bSubPlanningCustomerEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bSubPlanningCustomerVisible(): boolean {
        return this._evtType.bSubPlanningCustomerShowInd
    }

    get bCoverageFactorEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bCoverageFactorEventLevelVisible(): boolean {
        return this._evtType.bCoverageFactorShowInd && (this._evtType.cif.COVERAGEFACTOR_LEVEL == 'Event')
    }

    get bCoverageFactorProductGroupLevelVisible(): boolean {
        return this._evtType.bCoverageFactorShowInd
    }


    get bInStoreDatesEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this._evtType.cif.INSTOREDATES_POLICY == "UserInput" && this.bIsLockAcquired()
    }

    get bInStoreDatesVisible(): boolean {
        return this._evtType.bInStoreDatesShowInd
    }

    get bDistributionDatesEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this._evtType.cif.DISTRIBUTIONDATES_POLICY == "UserInput" && this.bIsLockAcquired()
    }

    get bDistributionDatesVisible(): boolean {
        return this._evtType.bDistributionDatesShowInd
    }

    get bShipmentDatesEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this._evtType.cif.SHIPMENTDATES_POLICY == "UserInput" && this.bIsLockAcquired()
    }

    get bShipmentDatesVisible(): boolean {
        return this._evtType.bShipmentDatesShowInd
    }

    get bOrderDatesEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this._evtType.cif.ORDERDATES_POLICY == "UserInput" && this.bIsLockAcquired()
    }

    get bOrderDatesVisible(): boolean {
        return this._evtType.bOrderDatesShowInd
    }

    get bCommentsEditable(): boolean {
        return (this.bIsEditor || this.bIsApprover) && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bCreatorVisible(): boolean {
        return this._evtType.bCreatorShowInd
    }


    get bCommentsVisible(): boolean {
        return this._evtType.bCommentsShowInd
    }

    get bTagsEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bTagsVisible(): boolean {
        return this._evtType.bTagsShowInd
    }

    get bProductGroupsEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bCalculateEnabled(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bSaveEnabled(): boolean {
        return (this.bIsEditor || this.bIsApprover) && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bProductsEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bProductIncludeEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }

    get bProductIncludeVisible(): boolean {
        return false
    }

    get bEventSpendEditable(): boolean {
        return this.bIsEditor && !this.bIsClosedOrCancelled && this.bIsLockAcquired()
    }


    get bWorkflowHistoryTabVisible(): boolean {
        return this.bIsPostApproval || this.bIsClosedOrCancelled
    }

    get PolicyChanged(): BehaviorSubject<IPolicy> {
        return this._PolicyChanged
    }

    private bIsLockAcquired(): boolean {
        let bIsLockedAcquired: boolean = false
        if (_.isEmpty(this._Lock))
            bIsLockedAcquired = true
        else
            bIsLockedAcquired = (this._Lock.Status == "Locked")

        return bIsLockedAcquired
    }

}


